import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { ArrowRight, ShieldCheck, ChevronRight } from "lucide-react";
import { GridBackground, NeonGlowOrbs } from "../components/GridBackground";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative isolate overflow-hidden border-b border-white/5"
    >
      <NeonGlowOrbs />
      <GridBackground />
      <div className="scanline absolute inset-0" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div
            data-testid="hero-tagline"
            className="inline-flex items-center gap-2 rounded-full border border-neon-green/30 bg-neon-green/5 px-3 py-1 font-mono text-xs text-neon-green"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neon-green" />
            // training the next 1M builders
          </div>

          <h1
            data-testid="hero-heading"
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            India&apos;s Future <span className="text-glow text-neon-green">AI &amp; Technology</span> Institute
          </h1>

          <div className="mt-5 min-h-[3.5rem] font-mono text-base text-white/70 md:text-lg">
            <span className="text-neon-green">$ </span>
            <TypeAnimation
              sequence={[
                "Learn AI, Coding, Digital Skills & Career-Ready Courses.",
                2000,
                "Industry-level training, real projects, verified certificates.",
                2000,
                "Freelancing • Startups • Job-Ready Skills.",
                2000,
              ]}
              speed={55}
              repeat={Infinity}
              cursor={true}
            />
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#courses" data-testid="hero-cta-courses" className="btn-neon">
              Explore Courses <ArrowRight size={18} className="ml-2" />
            </a>
            <Link
              to="/student-verification"
              data-testid="hero-cta-verify"
              className="btn-ghost-neon"
            >
              <ShieldCheck size={18} className="mr-2" /> Verify Certificate
            </Link>
          </div>

          <div className="mt-10 grid max-w-md grid-cols-3 gap-6">
            {[
              { n: "30+", l: "Courses" },
              { n: "10K+", l: "Students" },
              { n: "100%", l: "Verified" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l.toLowerCase()}`}>
                <div className="font-display text-2xl font-bold text-white">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-white/50">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Terminal block */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-2 rounded-2xl bg-neon-green/10 blur-2xl" />
            <div className="relative rounded-2xl border border-neon-green/30 bg-black/80 p-1 shadow-neon">
              <div className="flex items-center gap-1.5 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-soft-red/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-electric-blue/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-neon-green" />
                <span className="ml-3 font-mono text-xs text-white/40">gitsi@institute ~ %</span>
              </div>
              <div className="border-t border-white/5 px-5 py-5 font-mono text-[13px] leading-relaxed">
                <p className="text-white/50">// initializing future...</p>
                <p><span className="text-electric-blue">const</span> <span className="text-white">student</span> = <span className="text-neon-green">"you"</span>;</p>
                <p><span className="text-electric-blue">await</span> gitsi.<span className="text-neon-green">enroll</span>(student);</p>
                <p className="text-white/50">// loaded modules:</p>
                <p className="text-neon-green">✓ artificial-intelligence</p>
                <p className="text-neon-green">✓ full-stack-development</p>
                <p className="text-neon-green">✓ cyber-security</p>
                <p className="text-neon-green">✓ data-science</p>
                <p className="text-soft-red">{">"} ready to deploy career.</p>
                <p className="mt-2 text-white/70">
                  <span className="text-neon-green">$</span> _<span className="animate-pulse">|</span>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="glass rounded-xl px-4 py-3 text-xs text-white/70">
              <div className="font-mono text-neon-green">// industry</div>
              <div className="mt-1 font-medium">Real-world projects</div>
            </div>
            <div className="glass rounded-xl px-4 py-3 text-xs text-white/70">
              <div className="font-mono text-neon-green">// outcomes</div>
              <div className="mt-1 font-medium">Career &amp; freelancing</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
